/**
 * Tenant-uploaded SVG icons: the currency icon (`currency.iconSvg`), the chat
 * launcher icon (`intercom.launcherIconSvg`) and the opener logo
 * (`box.opener.logoSvg`, drawn onto every opener as the tenant's brand).
 *
 * Each is stored as SVG source on `storefront_configs` (a few KB of text — no
 * object storage in the path) and reaches browsers inside the storefront
 * config. Two rules keep that safe, and both must hold:
 *
 *  1. Renderers embed the icon as `<img src={svgIconDataUri(svg)}>`, never as
 *     inline markup. An SVG loaded through `<img>` runs in the browser's
 *     restricted mode: no scripts, no external fetches, no same-origin DOM.
 *     That is the boundary that actually holds. Swapping in
 *     `dangerouslySetInnerHTML` removes it and turns tenant input into XSS.
 *  2. `normalizeSvgIcon` runs on ingest and *rejects* anything carrying an
 *     executable or off-document construct. It never strips-and-keeps, so
 *     there is no sanitizer-mutation surface to bypass — a regex this misses
 *     lands in an `<img>`, where it is inert anyway.
 */

/** Ceiling on stored icon source. Icons are line art; 32 KB is generous. */
export const MAX_SVG_ICON_BYTES = 32_768;

/** Ceiling on the opener logo: a wordmark carries more paths than an icon, and rides on each box. */
export const MAX_SVG_LOGO_BYTES = 65_536;

export type SvgIconResult = { ok: true; svg: string } | { ok: false; reason: string };

/** Elements that execute code, embed a document, or pull in a subresource. */
const BLOCKED_ELEMENTS =
  /<\s*\/?\s*(script|foreignobject|iframe|embed|object|audio|video|handler|link|meta|base)\b/i;

/** Inline event handlers — `onload=`, `onclick=`, … */
const EVENT_HANDLER = /\son[a-z]+\s*=/i;

/** Script-bearing and document-bearing URL schemes. */
const BLOCKED_SCHEME = /(javascript|vbscript|livescript)\s*:|data\s*:\s*text\s*\/\s*html/i;

/** Every href/xlink:href in the document, with its quoted value. */
const HREF_ATTR = /\b(?:xlink:)?href\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi;

/** Every `url(...)` reference — CSS fills, filters, masks, markers. */
const URL_REF = /url\(\s*(['"]?)([^'")]*)\1\s*\)/gi;

function byteLength(input: string): number {
  return new TextEncoder().encode(input).length;
}

function unquote(value: string): string {
  const first = value[0];
  return first === '"' || first === "'" ? value.slice(1, -1) : value;
}

/**
 * Validates and normalizes an uploaded SVG. Returns the source to store, or
 * the reason it was refused (safe to show a user verbatim).
 *
 * An empty or whitespace-only input is valid and means "no custom icon" —
 * that is how the dashboard clears back to the default.
 */
export function normalizeSvgIcon(
  input: string,
  { maxBytes = MAX_SVG_ICON_BYTES }: { maxBytes?: number } = {},
): SvgIconResult {
  if (input.trim() === "") return { ok: true, svg: "" };

  // Checked against the raw input: entity declarations live in the DOCTYPE that
  // the next step strips, and stripping one would leave its `&refs;` dangling.
  if (/<!ENTITY/i.test(input)) {
    return { ok: false, reason: "SVG declares XML entities, which aren't allowed." };
  }

  // Comments and the XML prolog are stripped *before* scanning: leaving them
  // in lets `<scr<!---->ipt>` reach a browser that collapses it back.
  const svg = input
    .replace(/<\?[\s\S]*?\?>/g, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<!DOCTYPE[^>[]*(\[[\s\S]*?])?[^>]*>/gi, "")
    .trim();

  if (byteLength(svg) > maxBytes) {
    return {
      ok: false,
      reason: `SVG is larger than ${Math.floor(maxBytes / 1024)} KB. Simplify the artwork or run it through an SVG optimizer.`,
    };
  }
  if (!/^<svg[\s>]/i.test(svg) || !/<\/svg\s*>$/i.test(svg)) {
    return {
      ok: false,
      reason: "That file isn't an SVG — the markup must be a single root <svg> element.",
    };
  }
  if (BLOCKED_ELEMENTS.test(svg)) {
    return {
      ok: false,
      reason:
        "SVG contains scripting or embedded-document elements. Export it as flat shapes and try again.",
    };
  }
  if (EVENT_HANDLER.test(svg)) {
    return { ok: false, reason: "SVG contains event-handler attributes, which aren't allowed." };
  }
  if (BLOCKED_SCHEME.test(svg)) {
    return { ok: false, reason: "SVG references a script URL, which isn't allowed." };
  }
  if (/@import/i.test(svg)) {
    return { ok: false, reason: "SVG imports an external stylesheet, which isn't allowed." };
  }

  // References may only point inside this document, so the icon can't phone
  // home or break when the tenant's asset host goes away.
  for (const [, raw] of svg.matchAll(HREF_ATTR)) {
    if (
      !unquote(raw ?? "")
        .trim()
        .startsWith("#")
    ) {
      return {
        ok: false,
        reason:
          "SVG links to an external file. Embed the artwork directly instead of referencing it.",
      };
    }
  }
  for (const [, , target] of svg.matchAll(URL_REF)) {
    if (!(target ?? "").trim().startsWith("#")) {
      return {
        ok: false,
        reason:
          "SVG loads an external resource. Embed the artwork directly instead of referencing it.",
      };
    }
  }

  return { ok: true, svg };
}

/**
 * Icon source as an `<img src>` value. Percent-encoded rather than base64 —
 * same safety, roughly a third less payload than base64 on XML this repetitive.
 */
export function svgIconDataUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
